import { useEffect } from 'react';
import { useRefinementList } from 'react-instantsearch';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useRouter } from 'next/router';
import { useFilterContext } from '@context';
import { RefinementOption, RefinementSwatch } from '@components';
import { getFrameSizeOrder, normalizeRefinementItems, RefinementListProps, transformItems } from '@utils/algolia';
import {
	getColorSwapMap,
	getFrameColors,
	METAL_FRAME_COLORS,
	MIX_MATERIAL_SWATCHES_OPTIONS,
	NEW_METAL_FRAME_COLORS,
} from '@constants';
import RefinementListAccordion from '../RefinementListAccordion/RefinementListAccordion';

function RefinementList({ listType = 'accordion', className, setIsDisabled = null, ...props }: RefinementListProps) {
	const { attribute, operator, limit, showMore, showMoreLimit, type } = props;
	const { collections, isSunglassesRoute } = useFilterContext();
	const isHideNewColors = useFeatureIsOn('is-hide-new-products');
	const isColorRefresh = useFeatureIsOn('is-color-refresh-2');
	const { items, refine } = useRefinementList({
		attribute,
		operator,
		limit: showMore ? showMoreLimit : limit,
		transformItems,
	});
	const { locale } = useRouter();
	const FRAME_SIZE_ORDER = getFrameSizeOrder(locale);

	const handleOnRefine = (value: string) => {
		if (isColorRefresh && type === 'color' && METAL_FRAME_COLORS.some(metalColor => metalColor.includes(value))) {
			const mixMaterial = MIX_MATERIAL_SWATCHES_OPTIONS.find(option => option.acetate === value);
			mixMaterial?.metal && refine(mixMaterial.metal);
		}
		refine(value);
	};

	let optionsMap = normalizeRefinementItems({
		type,
		collections,
		items: items,
		isSunglasses: isSunglassesRoute,
		isHideNewProducts: isHideNewColors,
		locale,
	});

	// TODO: find out why frame_shape and frame_material are not sorted
	if (
		type === 'collections' ||
		type === 'color' ||
		type === 'frame_size' ||
		type === 'frame_material' ||
		type === 'frame_shape'
	) {
		optionsMap = new Map([...optionsMap].sort());
	}

	if (type === 'frame_size') {
		optionsMap = new Map(FRAME_SIZE_ORDER.filter(key => optionsMap.has(key)).map(key => [key, optionsMap.get(key)]));
	}

	if (type === 'product_type') {
		if (props.page === 'all-tops' || props.page === 'bf-all-tops') {
			optionsMap.delete('Top frame');
		}

		optionsMap = new Map(
			[...optionsMap].sort((a, b) => {
				if (a[0] > b[0]) return -1;
				if (a[0] < b[0]) return 1;
				return 0;
			})
		);
	}

	const refinementOptions = [];
	for (const [label, item] of optionsMap.entries()) {
		const { value, isRefined } = item;

		// If the refinement label is not found in the active collections,
		// then we would rather not render it
		if (label) {
			if (type === 'color') {
				const pushColor = () =>
					refinementOptions.push(
						<RefinementSwatch
							key={label}
							isRefined={isRefined}
							label={label}
							type={type}
							refine={handleOnRefine}
							value={value}
						/>
					);
				if (isColorRefresh) {
					pushColor();
				} else if (!NEW_METAL_FRAME_COLORS.includes(label)) {
					pushColor();
				}
			} else {
				refinementOptions.push(
					<RefinementOption
						key={label}
						isRefined={isRefined}
						label={label}
						type={type}
						refine={handleOnRefine}
						value={value}
					/>
				);
			}
		}
	}

	if (type === 'color')
		refinementOptions.sort((a, b) => {
			const aKey = a.key.includes('Mixed Material')
				? getFrameColors(locale, getColorSwapMap(isColorRefresh).Acetate[a.key])
				: a.key;
			const bKey = b.key.includes('Mixed Material')
				? getFrameColors(locale, getColorSwapMap(isColorRefresh).Acetate[b.key])
				: b.key;
			return aKey > bKey ? 1 : aKey < bKey ? -1 : 0;
		});

	// Disables the refinement list if there are no options
	useEffect(() => {
		if (typeof setIsDisabled === 'function') setIsDisabled(refinementOptions.length === 0);
	}, [refinementOptions.length, setIsDisabled]);

	const refinementListJsx =
		listType === 'accordion' ? (
			refinementOptions.length ? (
				<RefinementListAccordion refinementOptions={refinementOptions} isInitiallyOpened={type === 'color'} {...props} />
			) : null
		) : (
			<>
				<ul className={className}>{refinementOptions.slice(0, limit)}</ul>
				<div />
			</>
		);

	return !!items.length ? refinementListJsx : null;
}

export default RefinementList;
